import { CATEGORY_KEYS } from '../utils/propertyRequestForm';

// Explicit boundary between the React model and the ASP.NET multipart contract.
export const BIEN_API_FIELDS = {
  id: 'ID', libelle: 'Libelle', type: 'Type', surface: 'Surface',
  chambres: 'Chambres', sallesBains: 'SallesBains', parking: 'Parking',
  meuble: 'Meuble', adresse: 'Adresse', description: 'Description', offre: 'Offre',
  prix: 'Prix', prixReduit: 'PrixReduit', latitude: 'Latitude', longitude: 'Longitude',
  ville: 'Ville', delegation: 'Delegation', category: 'Category', email: 'Email',
  tel: 'Tel', codePostal: 'CodePostal', folder: 'Folder', urlImages: 'UrlImages', uid: 'uid',
};
const numbers = new Set(['surface', 'chambres', 'sallesBains', 'prix', 'prixReduit', 'latitude', 'longitude']);
const booleans = new Set(['parking', 'meuble', 'offre']);

export const isResidentialBien = (category) => ['appartement', 'maison'].includes(String(category || '').toLowerCase());
export const isLandBien = (category) => String(category || '').toLowerCase() === 'terrain';

// Display label of a canonical category ("Maison", "terrain"...) from the existing "categories.*" keys.
// The canonical value sent to the API is unchanged; an unknown category is shown as is.
export function bienCategoryLabel(t, category) {
  const value = String(category || '').trim();
  const canonical = Object.keys(CATEGORY_KEYS).find((name) => name.toLowerCase() === value.toLowerCase());
  return canonical ? t(`categories.${CATEGORY_KEYS[canonical]}`, value) : value;
}

// Omit unknown values: the current non-nullable API uses its defaults when absent.
export function bienSubmissionFields(model) {
  const data = normalizeBienForEdit(model);
  for (const key of ['prix', 'surface']) {
    if (data[key] == null || data[key] === '' || data[key] === 0) delete data[key];
    else if (data[key] < 0) throw new Error(`Invalid numeric field: ${key}`);
  }
  if (!isResidentialBien(data.category)) {
    delete data.chambres;
    delete data.meuble;
  }
  if (isLandBien(data.category)) {
    delete data.sallesBains;
    delete data.parking;
  }
  return data;
}

export function bienFieldValue(key, value) {
  if (value == null) return value;
  if (numbers.has(key)) {
    if (value === '') return '';
    if ((typeof value !== 'number' && typeof value !== 'string') ||
        String(value).trim() === '' || !Number.isFinite(Number(value))) {
      throw new Error(`Invalid numeric field: ${key}`);
    }
    return Number(value);
  }
  if (booleans.has(key)) {
    if (value === true || value === 'true') return true;
    if (value === false || value === 'false') return false;
    throw new Error(`Invalid boolean field: ${key}`);
  }
  if (typeof value !== 'string') throw new Error(`Invalid text field: ${key}`);
  return value;
}

export function normalizeBienForEdit(listing) {
  const result = {};
  for (const [key, apiKey] of Object.entries(BIEN_API_FIELDS)) {
    const aliases = [...new Set([key, apiKey, ...(key === 'id' ? ['Id'] : key === 'uid' ? ['Uid'] : [])])];
    const present = aliases.filter(alias => Object.prototype.hasOwnProperty.call(listing, alias));
    const values = present.map(alias => bienFieldValue(key, listing[alias]));
    if (values.some(value => value !== values[0])) throw new Error(`Conflicting field: ${key}`);
    result[key] = values[0];
  }
  return result;
}

export function buildBienUpdate(model, images) {
  const data = bienSubmissionFields(model);
  if (!data.offre) data.prixReduit = 0;
  if (data.offre && !(data.prix > 0 && data.prixReduit > 0 && data.prixReduit < data.prix)) {
    throw new Error('Invalid discount price');
  }

  for (const [key, value] of Object.entries(data)) {
    if (value === undefined || (numbers.has(key) && value === '')) {
      throw new Error(`Missing field: ${key}`);
    }
  }
  if (!Array.isArray(images) || images.some(image => typeof image !== 'string' && !(image instanceof Blob))) {
    throw new Error('Invalid images');
  }
  const retained = images.filter(image => typeof image === 'string').join(',');
  return { data: { ...data, urlImages: retained, allImages: retained }, files: images.filter(image => image instanceof Blob) };
}
